import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import block from 'bem-cn'
import dayjs from 'dayjs'

import { ReactComponent as CalendarIcon } from 'shared/img/calendar.svg'

import { Button, ButtonSizes, ButtonVariants } from 'components/UIKit/Button'
import { ControllerWrapper, ControllerWrapperSharedProps } from 'components/UIKit/ControllerWrapper'
import { InputDate } from 'components/UIKit/DatePicker/InputDate'
import { Dropdown, DropdownAnimationVariant, DropdownPositionStrategy } from 'components/UIKit/Dropdown'

import { ControlDateSelection } from '../ControlDateSelection'
import { useDatePickerContext } from '../DatePickerProvider'
import { DatesSelection } from '../DatesSelection'

import './DatePickerCalendarDate.scss'

const b = block('calendar-date')

export type DatePickerCalendarDateProps = ControllerWrapperSharedProps

const DatePickerCalendarDate: FC<DatePickerCalendarDateProps> = ({
  caption,
  description,
  isError,
  errorText,
  size,
  variant,
  isLoading,
  disabled,
  prefix,
  suffix,
}) => {
  const { controlDate, reset, value, modalControls, horizontalPosition, verticalPosition, isInputAvailable } =
    useDatePickerContext()
  const inputRef = useRef<HTMLInputElement>(null)
  const controllerRef = useRef<HTMLLabelElement | null>(null)
  const [triggerElement, setTriggerElement] = useState<HTMLLabelElement | null>(null)

  const setControllerRef = useCallback((node: HTMLLabelElement | null) => {
    controllerRef.current = node
    setTriggerElement(node)
  }, [])

  const inputValue = useMemo(() => {
    if (!value) {
      return modalControls.isOpened ? ' ' : ''
    }

    return dayjs(value as Date | null).format('DD/MM/YYYY')
  }, [value, modalControls.isOpened])

  useEffect(() => reset, [reset])

  const handleClick = (e: React.MouseEvent<HTMLLabelElement>) => {
    e.preventDefault()

    modalControls.togglePopup()
  }

  const handleOkClick = (e: React.SyntheticEvent) => {
    e.preventDefault()
    e.stopPropagation()
    modalControls.closePopup()
  }

  return (
    <ControllerWrapper
      className={b()}
      ref={setControllerRef}
      caption={caption}
      isError={isError}
      errorText={errorText}
      prefix={
        <>
          {prefix}
          {/* <CalendarIcon className={b('icon')} type="calendar" /> */}
        </>
      }
      size={size}
      isLoading={isLoading}
      suffix={
        <>
          {suffix}
          <CalendarIcon className={b('icon')} type="calendar" />
        </>
      }
      disabled={disabled}
      description={description}
      onClick={handleClick}
      variant={variant}>
      {isInputAvailable ? <InputDate ref={inputRef} /> : <div className={b('value')}>{inputValue}</div>}

      <Dropdown
        dropdownClassName={b('dropdown')}
        verticalPosition={verticalPosition}
        horizontalPosition={horizontalPosition}
        positionStrategy={DropdownPositionStrategy.FIXED}
        animation={DropdownAnimationVariant.NONE}
        popupControls={modalControls}
        triggerElement={triggerElement}>
        <div className={b('wrapper')}>
          <ControlDateSelection />
          <div className={b('dates-container')}>
            <DatesSelection date={controlDate} />
          </div>
          <div className={b('footer')}>
            <Button size={ButtonSizes.MEDIUM} variant={ButtonVariants.PRIMARY} text="Ok" onClick={handleOkClick} />
          </div>
        </div>
      </Dropdown>
    </ControllerWrapper>
  )
}

export { DatePickerCalendarDate }
